import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { tr } from '@util/tr';

export const PAYMENTS_DEFAULT_PAGE_SIZE = 10;

export interface PaymentFilter {
  customerName?: string;
  customerEmail?: string;
  saleId?: string;
  status?: '' | 'succeeded' | 'failed' | 'refunded' | 'pending';
  mode?: '' | 'card_present' | 'card';
  origin?: string;
  amountMin?: number;
  amountMax?: number;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

/** Counts active filters; date range and amount range each count as one filter. */
export function countPaymentFilters(filters: PaymentFilter | null | undefined): number {
  if (!filters) return 0;

  let count = 0;
  if (filters.customerName?.trim()) count++;
  if (filters.customerEmail?.trim()) count++;
  if (filters.saleId?.trim()) count++;
  if (filters.status) count++;
  if (filters.mode) count++;
  if (filters.origin?.trim()) count++;

  const hasAmountMin = filters.amountMin != null && !Number.isNaN(filters.amountMin);
  const hasAmountMax = filters.amountMax != null && !Number.isNaN(filters.amountMax);
  if (hasAmountMin || hasAmountMax) count++;

  if (filters.dateFrom || filters.dateTo) count++;

  return count;
}

interface SelectOption {
  id: string;
  name: string;
}

@Component({
  selector: 'web-payments-list-filter-dialog',
  templateUrl: './payments-list-filter-dialog.component.html',
  styleUrls: ['./payments-list-filter-dialog.component.scss'],
})
export class PaymentsListFilterDialogComponent {
  form: FormGroup;

  readonly statusOptions: SelectOption[] = [
    { id: '', name: tr('payments.list.filterStatusAll') },
    { id: 'succeeded', name: tr('payments.list.statusSucceeded') },
    { id: 'pending', name: tr('payments.list.statusPending') },
    { id: 'failed', name: tr('payments.list.statusFailed') },
    { id: 'refunded', name: tr('payments.list.statusRefunded') },
  ];

  readonly modeOptions: SelectOption[] = [
    { id: '', name: tr('payments.list.filterModeAll') },
    { id: 'card_present', name: 'Card Present' },
    { id: 'card', name: 'Card Not Present' },
  ];

  readonly originOptions: SelectOption[] = [
    { id: '', name: tr('payments.list.filterOriginAll') },
    { id: 'widget', name: tr('payments.list.originWidget') },
    { id: 'divinea', name: tr('payments.list.originDivinea') },
    { id: 'pos', name: tr('payments.list.originPos') },
    { id: 'self-order', name: tr('payments.list.originSelfOrder') },
    { id: 'moto', name: tr('payments.list.originMoto') },
    { id: 'payment-link', name: tr('payments.list.originPaymentLink') },
  ];

  readonly optionNameFormatter = (item: SelectOption | null): string => item?.name ?? '';

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { appliedFilters?: PaymentFilter },
    private dlg: MatDialogRef<PaymentsListFilterDialogComponent>,
    fb: FormBuilder
  ) {
    const applied = data?.appliedFilters ?? {};
    this.form = fb.group({
      customerName: [applied.customerName ?? ''],
      customerEmail: [applied.customerEmail ?? ''],
      saleId: [applied.saleId ?? ''],
      status: [this.findOption(this.statusOptions, applied.status)],
      mode: [this.findOption(this.modeOptions, applied.mode)],
      origin: [this.findOption(this.originOptions, applied.origin)],
      amountMin: [applied.amountMin ?? null],
      amountMax: [applied.amountMax ?? null],
      dateFrom: [applied.dateFrom ?? null],
      dateTo: [applied.dateTo ?? null],
    });
  }

  closeClicked(): void {
    this.dlg.close();
  }

  resetClicked(): void {
    this.dlg.close({});
  }

  submitClicked(): void {
    this.dlg.close({ ...this.buildFilters() });
  }

  onSelectorChange(fieldId: string, value: SelectOption | null): void {
    this.form.get(fieldId)?.setValue(value);
    this.form.get(fieldId)?.markAsDirty();
  }

  private buildFilters(): PaymentFilter {
    const v = this.form.getRawValue();
    return {
      customerName: this.asTrimmedString(v.customerName),
      customerEmail: this.asTrimmedString(v.customerEmail),
      saleId: this.asTrimmedString(v.saleId),
      status: this.resolveSelectId(v.status) as PaymentFilter['status'],
      mode: this.resolveSelectId(v.mode) as PaymentFilter['mode'],
      origin: this.resolveSelectId(v.origin),
      amountMin: v.amountMin != null && v.amountMin !== '' ? +v.amountMin : undefined,
      amountMax: v.amountMax != null && v.amountMax !== '' ? +v.amountMax : undefined,
      dateFrom: v.dateFrom ?? undefined,
      dateTo: v.dateTo ?? undefined,
    };
  }

  private asTrimmedString(value: unknown): string | undefined {
    if (value == null) {
      return undefined;
    }
    const trimmed = String(value).trim();
    return trimmed || undefined;
  }

  private resolveSelectId(value: SelectOption | string | null | undefined): string | undefined {
    if (value == null) {
      return undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed || undefined;
    }
    const id = value.id?.trim();
    return id || undefined;
  }

  private findOption(options: SelectOption[], id?: string): SelectOption {
    return options.find(o => o.id === (id ?? '')) ?? options[0];
  }
}
